<script lang="ts">
  // A suggested change as a small diff: the lines it replaces (−) and the
  // suggestion (+), colored by the engine. Rows are built from the code
  // text (renderLine escapes it), never from rendered Markdown.
  import type { Span } from '../engine/types';
  import { suggestionDiff } from '../logic/suggestion';
  import { renderLine } from '../logic/render';
  import { highlightText, languageOf } from '../diff/snippet';

  interface Props {
    /** The file the lines are from; its language colors them. */
    path: string;
    /** The lines the suggestion replaces; null when unknown (the suggestion shows alone). */
    original: readonly string[] | null;
    suggested: readonly string[];
    outdated?: boolean;
    /**
     * The suggestion's colors, from a caller that already highlights it (the
     * editor), with the lines they color. Without them this component asks
     * the engine itself.
     */
    suggestedColors?: Colors;
  }
  let { path, original, suggested, outdated = false, suggestedColors }: Props = $props();

  const rows = $derived(suggestionDiff(original ?? [], suggested));
  const note = $derived(
    original === null
      ? 'The lines it replaces are not known.'
      : suggested.length === 0
        ? original.length === 1
          ? 'Deletes the line.'
          : 'Deletes the lines.'
        : rows.every((r) => r.kind === 'context')
          ? 'No change.'
          : null,
  );

  /** One side's spans (null: plain), with the lines they were computed for. */
  type Colors = { lines: readonly string[]; spans: Span[][] | null };
  const PLAIN: Colors = { lines: [], spans: null };
  let oldColors: Colors = $state.raw(PLAIN);
  let ownColors: Colors = $state.raw(PLAIN);
  const newColors = $derived(suggestedColors ?? ownColors);

  async function light(p: string, lines: readonly string[]): Promise<Colors> {
    if (!lines.length) return PLAIN;
    return { lines, spans: await highlightText(await languageOf(p), lines.join('\n')) };
  }

  $effect(() => {
    const lines = original ?? [];
    let live = true;
    void light(path, lines).then((c) => {
      if (live) oldColors = c;
    });
    return () => (live = false);
  });

  $effect(() => {
    if (suggestedColors) return;
    const lines = suggested;
    let live = true;
    void light(path, lines).then((c) => {
      if (live) ownColors = c;
    });
    return () => (live = false);
  });

  /** A row's spans, while the side's colors still describe its text (an edit since leaves it plain until colored again). */
  function spans(c: Colors, at: number, text: string): Span[] {
    return c.spans && c.lines[at] === text ? (c.spans[at] ?? []) : [];
  }

  const SIGN = { context: ' ', del: '−', add: '+' } as const;
  const WORD = { context: 'unchanged', del: 'removed', add: 'added' } as const;
</script>

<div class="suggestion" class:outdated>
  <div class="suggestion-head">
    <span>Suggested change</span>
    {#if outdated}<span class="badge outdated" title="The lines this suggestion replaces have changed since it was written">outdated</span>{/if}
    {#if note}<span class="suggestion-note">{note}</span>{/if}
  </div>
  <div class="suggestion-rows">
    {#each rows as r, i (i)}
      <div class="suggestion-row {r.kind}"><span class="sign" aria-hidden="true">{SIGN[r.kind]}</span><code><span class="sr-only">{WORD[r.kind]}: </span>{@html renderLine(r.text, spans(r.kind === 'add' ? newColors : oldColors, r.at, r.text))}</code></div>
    {/each}
  </div>
</div>
